import { useCallback, useState } from 'react';

import { ViewIcon } from '@hugeicons/core-free-icons';
import { isScreeningAttentionVisible } from 'helpers/screeningVerificationBadge';
import { useLocale } from 'hooks/locale';
import Moment from 'react-moment';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { RenewalResultsModal } from 'views/Renewals/components/RenewalResultsModal/RenewalResultsModal';

import { Select } from 'components/FormComponents';
import { IconComponent } from 'components/Icon/IconComponent';
import { StatusPill } from 'components/Pills/StatusPills';
import { Table } from 'components/Table/Table';
import { VerificationBadge } from 'components/VerificationBadge/VerificationBadge';

import { useUpdateCandidateCheckMutation } from 'api/candidate-checks-api';
import { selectCompanyChecks } from 'api/company-checks-slice';
import { useLazyGetEmployeesResultsQuery } from 'api/employees-api';

import styles from './RenewalResultsTab.module.scss';

export const RenewalResultsTab = ({ company, setLoading = () => {} }) => {
  const { dateSlashFormat } = useLocale(company?.locale);

  const companyChecks = useSelector(selectCompanyChecks);

  const [getEmployeesResults] = useLazyGetEmployeesResultsQuery();
  const [updateCandidateCheck] = useUpdateCandidateCheckMutation();

  const [pagination, setPagination] = useState({ page: 1, total_pages: 1 });
  const [results, setResults] = useState([]);
  const [selectedResult, setSelectedResult] = useState(null);

  const onQueryUpdate = async params => {
    setLoading(true);
    const { data } = await getEmployeesResults({ params });
    setResults(data.results);
    setPagination(data.pagination);
    setLoading(false);
  };

  const getCheckType = useCallback(
    type => {
      const companyCheck = companyChecks.find(companyCheck => companyCheck.check_type.slug == type);
      return companyCheck?.check_type;
    },
    [companyChecks],
  );

  const onChangeStatus = async (candidateCheckId, status) => {
    setLoading(true);
    await updateCandidateCheck({ id: candidateCheckId, data: { user_review_status: status } });
    setResults(results =>
      results.map(result =>
        result.candidate_check_id === candidateCheckId
          ? { ...result, user_review_status: status }
          : result,
      ),
    );
    setSelectedResult(null);
    setLoading(false);
  };

  return (
    <>
      <RenewalResultsModal
        result={selectedResult}
        visible={!!selectedResult}
        onClose={() => setSelectedResult(null)}
        onStatusChange={onChangeStatus}
      />
      <Table.Context
        onQueryUpdate={onQueryUpdate}
        defaultQuery={{
          page: 1,
          when: '30',
          user_review_status: 'add_review',
          result_flag: 'false',
        }}>
        <Table.FloatingFilters className="u-margin--large surface-white">
          <Table.FloatingFiltersLeft className="u-gap-medium align-items-end">
            <Table.MultiSelectFilter
              title="Approval Status"
              filterName="user_review_status"
              type="status"
              options={[
                { name: 'Needs Review', value: 'add_review' },
                { name: 'Not Approved', value: 'not_approved' },
                { name: 'Approved', value: 'approved' },
              ]}
              useDefault={false}
            />
            <Table.MultiSelectFilter
              title="Results"
              filterName="result_flag"
              type="status"
              options={[
                { name: 'Has Results', value: 'true' },
                { name: 'No Results', value: 'false' },
              ]}
            />
            <Table.MultiSelectFilter
              title="Attention Flag"
              filterName="attention_flag"
              type="status"
              options={[
                { name: 'Needs Attention', value: 'true' },
                { name: 'No Attention', value: 'false' },
              ]}
              useDefault={false}
            />
          </Table.FloatingFiltersLeft>
        </Table.FloatingFilters>
        <Table.TableContent className="u-margin--large">
          <Table.Filters>
            <Table.Search placeholder="Search for an employee..." />
          </Table.Filters>
          <Table className="u-margin--large">
            <Table.Head>
              <Table.Row>
                <Table.Header>Status</Table.Header>
                <Table.Header>Employee</Table.Header>
                <Table.Header>Check Type</Table.Header>
                <Table.Header>Renewal Config</Table.Header>
                <Table.Header>Completed At</Table.Header>
                <Table.Header>Next Renew At</Table.Header>
                <Table.Header>Review Status</Table.Header>
                <Table.Header>Actions</Table.Header>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {results?.map(result => (
                <Table.Row key={result.candidate_check_id}>
                  <Table.Col styles={{ 'vertical-align': 'center' }}>
                    <div className="d-flex align-items-center gap-2">
                      <StatusPill
                        status={result.result_flag ? 'error' : 'success'}
                        text={
                          result.result_flag
                            ? getCheckType(result.type)?.true_flag_label
                            : getCheckType(result.type)?.false_flag_label
                        }
                      />
                      {isScreeningAttentionVisible(
                        {
                          canceled: false,
                          attention_flag: result.attention_flag,
                          completed_at: result.completed_at,
                          result_flag: result.result_flag,
                        },
                        getCheckType(result.type),
                      ) && (
                        <VerificationBadge
                          type="attention"
                          tip={
                            getCheckType(result.type)?.attention_flag_config?.attention_label ||
                            'Needs attention'
                          }
                        />
                      )}
                    </div>
                  </Table.Col>
                  <Table.Col>
                    <Link to={`/employee/${result.employee_id}`} className="u-link dark">
                      {result.candidate_name}
                    </Link>
                  </Table.Col>
                  <Table.Col styles={{ 'vertical-align': 'center' }}>
                    {getCheckType(result.type)?.title}
                  </Table.Col>
                  <Table.Col styles={{ 'vertical-align': 'center' }}>
                    {result.renewal_config_name}
                  </Table.Col>
                  <Table.Col styles={{ 'vertical-align': 'center' }}>
                    {result.completed_at ? (
                      <Moment format={dateSlashFormat}>{result.completed_at}</Moment>
                    ) : (
                      '-'
                    )}
                  </Table.Col>
                  <Table.Col styles={{ 'vertical-align': 'center' }}>
                    {result.next_renew_at ? (
                      <Moment format={dateSlashFormat}>{result.next_renew_at}</Moment>
                    ) : (
                      '-'
                    )}
                  </Table.Col>
                  <Table.Col>
                    <Select
                      className={styles.reviewStatusSelect}
                      inputProps={{
                        onChange: e => onChangeStatus(result.candidate_check_id, e.target.value),
                      }}
                      value={result.user_review_status}
                      placeholder="Add Review"
                      rounded
                      small>
                      <Select.Item value="approved">Approved</Select.Item>
                      <Select.Item value="not_approved">Not Approved</Select.Item>
                      <Select.Item value="needs_review">Needs Review</Select.Item>
                    </Select>
                  </Table.Col>
                  <Table.Col styles={{ 'vertical-align': 'center' }}>
                    <IconComponent
                      type="neutral-light"
                      icon={ViewIcon}
                      noBackground
                      tip="View result"
                      onClick={() => setSelectedResult(result)}
                    />
                  </Table.Col>
                </Table.Row>
              ))}
              {results.length < 1 && <Table.Placeholder>No employees found</Table.Placeholder>}
            </Table.Body>
            <Table.Pagination {...pagination} />
          </Table>
        </Table.TableContent>
      </Table.Context>
    </>
  );
};
